/**
 * Hosted payment URL validation.
 *
 * Every card page the DriverApp opens comes from AutoDispatch over the socket
 * (`tripCollectionUrl`), which in turn got it from the HBSS gateway. Nothing
 * between the gateway and this component validates it, and the value ends up in
 * `window.open()` and an `<a href>` — so an unexpected value is opened
 * verbatim. Three failure modes matter:
 *
 *  1. **A non-HTTPS URL.** Card details would be typed over a channel that can
 *     be read and modified in transit. A payment page must never be http:.
 *  2. **A dangerous scheme.** `javascript:` in an href executes in this origin;
 *     `data:`/`blob:` can render an attacker-authored page that looks like the
 *     real card form. Both are reachable if the string is opened unchecked.
 *  3. **A relative or malformed value.** It resolves against the DriverApp's own
 *     origin, so the driver gets a 404 inside the app — visually identical to a
 *     blocked pop-up, which is how this class of bug hides.
 *
 * Only http(s) is parsed, and only https is accepted for a payment page. The
 * RiderApp applies the same rule in components/HostedPaymentPopup.jsx.
 */

// Reasons a URL was refused, so callers can explain rather than fail silently.
export const HOSTED_URL_REJECTION = {
  EMPTY: 'empty',
  MALFORMED: 'malformed',
  INSECURE: 'insecure',
  UNSUPPORTED_SCHEME: 'unsupportedScheme',
};

const SAFE_PROTOCOLS = ['http:', 'https:'];

/**
 * @param {unknown} raw
 * @param {{ allowInsecure?: boolean }} [options]  allowInsecure permits http:
 *   for local development only. Never enable it for a real card page.
 * @returns {{ ok: true, url: URL, href: string, host: string }
 *          | { ok: false, reason: string, received: string }}
 */
export function parseHostedPaymentUrl(raw, options = {}) {
  const received = raw === null || raw === undefined ? '' : String(raw).trim();
  if (!received) return { ok: false, reason: HOSTED_URL_REJECTION.EMPTY, received };

  let url;
  try {
    url = new URL(received);
  } catch (e) {
    // Deliberately NOT resolved against window.location: silently sending the
    // driver to our own 404 is worse than reporting a bad address.
    return { ok: false, reason: HOSTED_URL_REJECTION.MALFORMED, received };
  }

  if (!SAFE_PROTOCOLS.includes(url.protocol)) {
    // javascript:, data:, blob:, file: … — never openable as a payment page.
    return { ok: false, reason: HOSTED_URL_REJECTION.UNSUPPORTED_SCHEME, received };
  }

  if (url.protocol !== 'https:' && options.allowInsecure !== true) {
    return { ok: false, reason: HOSTED_URL_REJECTION.INSECURE, received };
  }

  return { ok: true, url, href: url.href, host: url.host };
}

/** True when the value is safe to hand to window.open / an href. */
export function isSafeHostedPaymentUrl(raw, options) {
  return parseHostedPaymentUrl(raw, options).ok === true;
}

/** Driver-facing explanation for a refused URL. */
export function hostedUrlRejectionMessage(reason) {
  switch (reason) {
    case HOSTED_URL_REJECTION.INSECURE:
      return 'The payment page address is not secure (it must use https), so it was not opened. Please collect cash or contact dispatch.';
    case HOSTED_URL_REJECTION.UNSUPPORTED_SCHEME:
      return 'The payment page address is not a valid web address, so it was not opened. Please collect cash or contact dispatch.';
    case HOSTED_URL_REJECTION.MALFORMED:
      return 'The payment page address returned by dispatch is unusable, so it was not opened. Please try again or collect cash.';
    case HOSTED_URL_REJECTION.EMPTY:
    default:
      return 'No payment page address was returned, so nothing could be opened. Please try again.';
  }
}
